import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useBillingUsage } from "@/hooks/useBilling";
import { format, formatDistanceToNow } from "date-fns";
import { CreditCard, Package, AlertCircle, Clock, Gift, Zap } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export function BillingOverviewTab() {
  const { data: usage, isLoading } = useBillingUsage();

  if (isLoading || !usage) {
    return <div>Loading...</div>;
  }

  // Check if user is on free plan with expiring credits or in free trial
  const isFreePlan = usage.subscription?.name?.toLowerCase().includes('free') || usage.freeTrialInfo?.isInFreeTrial;
  const isInFreeTrial = usage.freeTrialInfo?.isInFreeTrial;

  // Calculate usage percentages for each credit type
  const subscriptionPercentage = usage.subscriptionCredits && usage.subscriptionCredits.allocated > 0
    ? (usage.subscriptionCredits.used / usage.subscriptionCredits.allocated) * 100
    : 0;
  
  const boostPackPercentage = usage.boostPackCredits.purchased > 0
    ? (usage.boostPackCredits.used / usage.boostPackCredits.purchased) * 100
    : 0;

  // Use real free trial data from API
  const trialExpirationDate = usage.freeTrialInfo?.trialEndDate ? new Date(usage.freeTrialInfo.trialEndDate) : null;
  const daysUntilExpiration = usage.freeTrialInfo?.daysRemaining;

  return (
    <div className="space-y-6">
      {/* Free Trial Expiration Warning */}
      {isInFreeTrial && trialExpirationDate && daysUntilExpiration && daysUntilExpiration <= 3 && (
        <Alert variant="destructive">
          <Clock className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              {usage.freeTrialInfo?.trialType === 'stripe' ? (
                <>
                  <strong>Your plan activates {daysUntilExpiration === 1 ? 'tomorrow' : `in ${daysUntilExpiration} days`}!</strong>
                  <br />
                  Your subscription will begin automatically.
                </>
              ) : (
                <>
                  <strong>Your free credits expire {daysUntilExpiration === 1 ? 'tomorrow' : `in ${daysUntilExpiration} days`}!</strong>
                  <br />
                  Choose a plan to keep your AI Employee active.
                </>
              )}
            </div>
            {usage.freeTrialInfo?.trialType !== 'stripe' && (
              <Button variant="destructive" size="sm" className="ml-4">
                Choose Plan
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Free Plan Info (when not urgent) */}
      {isInFreeTrial && trialExpirationDate && daysUntilExpiration && daysUntilExpiration > 3 && (
        <Alert>
          <Gift className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                {usage.freeTrialInfo?.trialType === 'stripe' ? (
                  <>You're on the <strong>Free Trial</strong>. Your plan activates on {format(trialExpirationDate, 'MMM d, yyyy')}.</>
                ) : (
                  <>You're using <strong>Free Credits</strong>. They expire on {format(trialExpirationDate, 'MMM d, yyyy')}.</>
                )}
              </div>             
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Delinquency Alert */}
      {usage.isDelinquent && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your account is delinquent. Please add credits to restore service.
          </AlertDescription>
        </Alert>
      )}

      {/* Low Credit Warning */}
      {usage.creditsRemaining < 50 && usage.creditsRemaining > 0 && !isFreePlan && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your credit balance is low. Consider purchasing a boost pack to avoid service interruption.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Credit Overview */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Subscription Credits Card - Only show if user has a subscription plan with credits */}
        {usage.subscriptionCredits && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isInFreeTrial ? <Gift className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                {isInFreeTrial 
                  ? (usage.freeTrialInfo?.trialType === 'stripe' ? 'Trial Credits' : 'Free Credits')
                  : 'Subscription Credits'
                }
                {isInFreeTrial && (
                  <Badge variant="secondary" className="text-xs">
                    {usage.freeTrialInfo?.trialType === 'stripe' ? 'Trial' : 'Free'}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {usage.subscription?.name || 'Monthly allocation'}
                {isInFreeTrial && trialExpirationDate && (
                  <div className="mt-1 text-sm text-destructive">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Expires {formatDistanceToNow(trialExpirationDate, { addSuffix: true })}
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Balance</span>
                  <span className="font-medium">
                    {usage.subscriptionCredits.remaining.toFixed(1)} / {usage.subscriptionCredits.allocated.toFixed(1)} credits
                  </span>
                </div>
                <Progress
                  value={subscriptionPercentage}
                  className={`h-2 ${isInFreeTrial && daysUntilExpiration && daysUntilExpiration <= 3 ? 'bg-destructive/10' : ''}`}
                />
                <p className="text-xs text-muted-foreground">
                  {usage.subscriptionCredits.used.toFixed(1)} used {isInFreeTrial 
                    ? (usage.freeTrialInfo?.trialType === 'stripe' ? 'from trial' : 'from free credits')
                    : 'this period'}
                </p>
              </div>
              
              {/* Billing Period Info */}
              <div className="pt-2 border-t">
                {isInFreeTrial && trialExpirationDate ? (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {usage.freeTrialInfo?.trialType === 'stripe' 
                        ? `Plan activates ${format(trialExpirationDate, 'MMM d, yyyy')}`
                        : `Credits expire ${format(trialExpirationDate, 'MMM d, yyyy')}`
                      }
                    </p>
                    {daysUntilExpiration && daysUntilExpiration <= 7 && (
                      <Badge variant={daysUntilExpiration <= 3 ? "destructive" : "secondary"} className="text-xs">
                        {daysUntilExpiration} days left
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Resets {format(new Date(usage.billingPeriodEnd), 'MMM d, yyyy')}
                  </p>
                )}
              </div>

              {/* Upgrade CTA for boost pack trials only */}
              {isInFreeTrial && usage.freeTrialInfo?.trialType === 'boost_pack' && (
                <div className="pt-2 border-t">
                  <Button size="sm" className="w-full">
                    <Zap className="h-3 w-3 mr-1" />
                    Choose Plan to Continue
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Boost Pack Credits Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Boost Pack Credits
            </CardTitle>
            <CardDescription>
              One-time credit purchases {isFreePlan && '(extend your trial)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Balance</span>
                <span className="font-medium">
                  {usage.boostPackCredits.remaining.toFixed(1)} credits
                </span>
              </div>
              {usage.boostPackCredits.purchased > 0 ? (
                <>
                  <Progress value={boostPackPercentage} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {usage.boostPackCredits.used.toFixed(1)} of {usage.boostPackCredits.purchased.toFixed(1)} used
                  </p>
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    No boost packs purchased yet
                  </p>
                  {isFreePlan && (
                    <p className="text-xs text-primary">
                      💡 Boost packs never expire and extend beyond your trial
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Purchase CTA for free plan */}
            {isFreePlan && usage.boostPackCredits.purchased === 0 && (
              <div className="pt-2 border-t">
                <Button variant="outline" size="sm" className="w-full">
                  <CreditCard className="h-3 w-3 mr-1" />
                  Get Boost Pack Credits
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscription Plan Features Card - Only show if features exist */}
      {usage.subscription?.features && usage.subscription.features.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {usage.subscription.name} Features
            </CardTitle>
            <CardDescription>
              What's included with your subscription plan
              {(usage.subscription.priceOneTime || usage.subscription.priceMonthly) && (
                <div className="mt-1 text-sm font-medium text-foreground">
                  {usage.subscription.priceOneTime && usage.subscription.priceMonthly ? (
                    <div>
                      <span>${usage.subscription.priceOneTime} one-time + ${usage.subscription.priceMonthly}/month</span>
                    </div>
                  ) : usage.subscription.priceOneTime ? (
                    <div>
                      <span>${usage.subscription.priceOneTime} one-time</span>
                      <span className="text-success ml-2">• Lifetime access</span>
                    </div>
                  ) : (
                    <span>${usage.subscription.priceMonthly}/month</span>
                  )}
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {usage.subscription.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-sm">
                  <div className="h-2 w-2 bg-success rounded-full mt-1.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {usage.subscription.description && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  {usage.subscription.description}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your recent credit transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {usage.recentTransactions.length === 0 ? (
              <p className="text-muted-foreground text-sm">No recent transactions</p>
            ) : (
              usage.recentTransactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{transaction.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(transaction.createdAt), 'MMM d, h:mm a')}
                    </p>
                  </div>
                  <div className={`text-sm font-medium ${
                    transaction.creditsAmount > 0 ? 'text-success' : 'text-destructive'
                  }`}>
                    {transaction.creditsAmount > 0 ? '+' : ''}{transaction.creditsAmount.toFixed(1)}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 